// Central source of truth for all comparison page data.
// Freshdesk and Zendesk pricing re-checked 2026-10-19 on their official pricing pages
// (freshworks.com/freshdesk/pricing, zendesk.com/pricing). Jira Service Management re-checked
// 2026-10-21 on atlassian.com/collections/service/pricing (15 agents, annual billing: Standard
// $4,050/yr = $22.50 per agent per month, Premium $9,200/yr = about $51.10; price depends on agent tier).
// Zoho Desk re-checked 2026-10-21: zoho.com/desk/pricing.html shows only INR from India (annual: Express 420,
// Standard 800, Professional 1,400, Enterprise 2,400). USD annual prices $7/$14/$23/$40 per user per month are on
// Zoho's own pages zoho.com/desk/express (Express $7 annual, $9 monthly, 5-user limit), zoho.com/desk/zendesk-alternative
// and zoho.com/desk/hiver-alternative, and match the INR annual ratios. Re-verify quarterly.

export type FeatureValue = boolean | '~' | string;

export type FeatureRow = {
  label: string;
  kora: FeatureValue;
  competitor: FeatureValue;
};

export type FeatureCategory = {
  category: string;
  rows: FeatureRow[];
};

export type PlanPricing = {
  name: string;
  price: number; // per agent/month, annual billing (0 = free)
  priceNote?: string;
  highlight?: boolean;
};

export type CompetitorData = {
  slug: string;
  name: string;
  tagline: string;
  bestFor: string;
  pricingModel: string;
  // Optional SEO overrides for the page <title> and meta description
  metaTitle?: string;
  metaDescription?: string;
  plans: PlanPricing[];
  // Cost at team size (annual billing, per agent)
  costAt: (agents: number) => number;
  // Kora cost is always 39 (Growth flat) or 0 (Free)
  verdictKora: string;
  verdictCompetitor: string;
  koraWins: string[];
  competitorWins: string[];
  switchingReasons: string[];
  faqs: { q: string; a: string }[];
  features: FeatureCategory[];
  // For at-a-glance summary
  atAGlance: { label: string; kora: string | boolean; competitor: string | boolean }[];
};

// ─── Kora (shared across all comparisons) ───────────────────

export const KORA = {
  name: 'Kora',
  freePrice: 0,
  growthPrice: 0, // pay-as-you-go — no plan price
  // Estimated monthly cost at typical volume: ~30 tickets + 10 AI answers
  // per agent/mo = 40 credits = $4/agent/mo. Usage-based, not flat.
  costAt: (agents: number) => Math.round(agents * 4),
  freePlan: 'Starts free — 1,000 bonus credits (30 days) + 100 credits (10 years)',
  growthPlan: 'Pay as you go — $1 = 10 credits, 1 credit per ticket created',
};

// ─── Freshdesk ──────────────────────────────────────────────

export const freshdesk: CompetitorData = {
  slug: 'kora-vs-freshdesk',
  name: 'Freshdesk',
  tagline: 'Omnichannel help desk by Freshworks',
  bestFor: 'Mid-large support teams needing omnichannel (email, chat, phone, social) and a mature app marketplace.',
  pricingModel: 'Per agent / month',
  plans: [
    { name: 'Growth', price: 19, priceNote: 'Annual billing' },
    { name: 'Pro', price: 55, priceNote: 'Annual billing', highlight: true },
    { name: 'Enterprise', price: 89, priceNote: 'Annual billing' },
  ],
  costAt: (agents: number) => agents * 55, // Pro tier
  verdictKora: 'Small-to-mid teams who want AI-first ticketing, usage-based pricing, and embedded sessions without per-seat costs.',
  verdictCompetitor: 'Established support teams needing true omnichannel (phone, chat, social), a knowledge base, and 1000+ marketplace apps.',
  koraWins: [
    'Pay-as-you-go credit pricing — no per-seat scaling costs',
    'AI ticket structuring is free — no per-seat or add-on fee',
    'No per-seat fees and no per-resolution billing — Kora charges $0.10 per ticket created',
    '1,100 free credits to start, no trial clock',
    'Embedded sessions for in-app support',
    'Public REST API — 20+ endpoints, available to every workspace',
  ],
  competitorWins: [
    'Native omnichannel today: email, live chat, phone, social media (Kora: email-to-ticket, Slack and live chat are planned; phone and social are not on the roadmap)',
    'Built-in knowledge base and customer portal (Kora: included)',
    'CSAT surveys (Kora: on the roadmap; SLA tracking is free in Kora)',
    '1000+ marketplace apps and integrations',
    'Sandbox and audit logs on Enterprise',
  ],
  switchingReasons: [
    'Per-seat pricing becomes unpredictable as the team grows — 15 agents on Pro costs $825/mo',
    'Freddy AI is a separate add-on ($29/agent/mo for Copilot, $49/100 sessions for AI Agent)',
    'Setup complexity increases significantly at Pro and Enterprise tiers',
    'You want AI-first ticket structuring without paying extra for AI',
  ],
  faqs: [
    {
      q: 'Is Kora really cheaper than Freshdesk?',
      a: 'For most teams, yes. Kora is pay-as-you-go: $1 buys 10 credits and a ticket costs 1 credit — a 15-agent team doing ~450 tickets/month pays roughly $45/mo. Freshdesk Pro is $55/agent/mo on annual billing — 15 agents costs $825/mo (Freshdesk pricing page, checked 2026-10-19). Kora also starts with 1,100 free credits and has no trial clock.',
    },
    {
      q: 'Does Kora have live chat and phone support like Freshdesk?',
      a: 'Live chat is planned for Kora; telephony and social media channels are not on the roadmap. Today, Kora focuses on AI-structured ticketing via paste, API, and webhooks. If native omnichannel is essential today, Freshdesk is the stronger choice.',
    },
    {
      q: 'Can I import my tickets from Freshdesk?',
      a: 'Yes. Export your Freshdesk tickets as CSV, then paste raw descriptions into Kora (AI will structure them) or use the Public API to create tickets programmatically. Comments and attachments can be migrated via the API.',
    },
    {
      q: "What's the catch with Kora's free credits?",
      a: 'No catch. You start with 1,100 free credits (1,000 for 30 days plus 100 that last 10 years) — no credit card, no trial clock. Credits fund what you use: 1 per ticket created, 1 per AI Analyst answer. Public API, embedded sessions, CSV export, and team analytics are included. Top up from $5 whenever you need more.',
    },
    {
      q: 'Does Kora integrate with Freshworks apps?',
      a: 'Kora has outbound webhooks and a Public REST API (20+ endpoints) for every workspace. It does not have native integrations with Freshsales, Freshchat, or the Freshworks marketplace. If you rely on the Freshworks ecosystem, Freshdesk is the better fit.',
    },
    {
      q: 'Which is better for a small team?',
      a: 'For teams that want AI to structure tickets and pay per ticket instead of per seat, Kora. For teams who need omnichannel support (chat, phone, email, social) and a mature knowledge base, Freshdesk — but expect per-seat costs to scale.',
    },
  ],
  atAGlance: [
    { label: 'Pricing model', kora: 'Pay-as-you-go ($1 = 10 credits)', competitor: 'Per agent/mo' },
    { label: 'Free to start', kora: '1,100 free credits', competitor: '14-day trial (no free plan listed)' },
    { label: 'AI ticket structuring', kora: 'Free (no add-on fee)', competitor: 'Add-on ($29/agent/mo)' },
    { label: 'AI Analyst', kora: '1 credit per answer', competitor: 'Freddy AI add-on' },
    { label: 'Public API (20+ endpoints)', kora: 'Every workspace', competitor: 'Growth and above' },
    { label: 'Embedded sessions', kora: 'Yes', competitor: 'No' },
    { label: 'Omnichannel', kora: 'Planned (email-to-ticket, Slack, live chat)', competitor: 'Native (email, chat, phone, social)' },
    { label: 'Knowledge base', kora: true, competitor: 'Yes' },
    { label: 'SLA tracking', kora: 'Free, every workspace', competitor: 'Yes (Growth+)' },
    { label: 'EU data residency', kora: 'Yes (Cloudflare D1, EU)', competitor: 'Varies by region' },
  ],
  features: [
    {
      category: 'Ticketing & Workflow',
      rows: [
        { label: 'Ticket creation', kora: true, competitor: true },
        { label: 'Ticket assignment', kora: 'Claim model', competitor: 'Round-robin, load-balanced, skill-based' },
        { label: 'Ticket merging', kora: true, competitor: true },
        { label: 'Bulk ticket actions', kora: true, competitor: true },
        { label: 'Custom workflows', kora: '~', competitor: true },
        { label: 'SLA tracking', kora: 'Free, every workspace', competitor: true },
        { label: 'CSAT surveys', kora: false, competitor: true },
      ],
    },
    {
      category: 'AI',
      rows: [
        { label: 'AI ticket structuring', kora: 'Free (no add-on fee)', competitor: 'Add-on ($29/agent/mo)' },
        { label: 'AI Analyst (text-to-SQL)', kora: '1 credit per answer', competitor: false },
        { label: 'AI Copilot (agent assist)', kora: 'Kora AI draft reply: 1 credit', competitor: 'Add-on ($29/agent/mo)' },
        { label: 'AI Agent (deflection)', kora: 'Kora AI agent: 3 credits per confirmed resolution', competitor: 'Add-on ($49/100 sessions)' },
      ],
    },
    {
      category: 'Collaboration',
      rows: [
        { label: 'Comments & attachments', kora: true, competitor: true },
        { label: 'Real-time updates', kora: true, competitor: true },
        { label: 'Internal notes', kora: true, competitor: true },
        { label: '@mentions', kora: true, competitor: true },
      ],
    },
    {
      category: 'Integrations & API',
      rows: [
        { label: 'Public REST API (20+ endpoints)', kora: 'Every workspace', competitor: 'Growth and above' },
        { label: 'Webhooks (outbound)', kora: true, competitor: true },
        { label: 'Marketplace apps', kora: 'Not available', competitor: '1000+ apps' },
        { label: 'Custom API endpoints', kora: false, competitor: false },
      ],
    },
    {
      category: 'Embedding',
      rows: [
        { label: 'Embedded sessions (iframe)', kora: true, competitor: false },
        { label: 'Customer portal', kora: true, competitor: true },
        { label: 'White-label', kora: '~', competitor: 'Enterprise' },
      ],
    },
    {
      category: 'Analytics',
      rows: [
        { label: 'Dashboards', kora: true, competitor: true },
        { label: 'Custom reports', kora: '1 credit per answer via AI Analyst', competitor: 'Pro+' },
        { label: 'CSV export', kora: true, competitor: true },
        { label: 'Team analytics', kora: true, competitor: true },
      ],
    },
    {
      category: 'Channels',
      rows: [
        { label: 'Email', kora: 'Outbound notifications', competitor: 'Native inbound + outbound' },
        { label: 'Live chat', kora: 'Planned', competitor: true },
        { label: 'Phone / telephony', kora: 'Not available', competitor: true },
        { label: 'Social media', kora: 'Not available', competitor: true },
        { label: 'Web forms', kora: 'Public API', competitor: true },
      ],
    },
    {
      category: 'Security & Compliance',
      rows: [
        { label: 'SSO', kora: false, competitor: 'Enterprise' },
        { label: 'Audit logs', kora: 'Ticket activity log only', competitor: 'Enterprise' },
        { label: 'Sandbox', kora: false, competitor: 'Enterprise' },
        { label: 'EU data residency', kora: true, competitor: '~' },
        { label: 'Encryption at rest', kora: true, competitor: true },
      ],
    },
  ],
};

// ─── Zoho Desk ──────────────────────────────────────────────

export const zohoDesk: CompetitorData = {
  slug: 'kora-vs-zoho-desk',
  name: 'Zoho Desk',
  tagline: 'Help desk by Zoho',
  bestFor: 'Budget-conscious teams already in the Zoho ecosystem who need multi-department support and SLA management.',
  pricingModel: 'Per agent / month',
  plans: [
    { name: 'Free', price: 0, priceNote: 'Up to 3 agents' },
    { name: 'Express', price: 7, priceNote: 'Up to 5 agents' },
    { name: 'Standard', price: 14, priceNote: 'Unlimited agents' },
    { name: 'Professional', price: 23, priceNote: 'Unlimited agents', highlight: true },
    { name: 'Enterprise', price: 40, priceNote: 'Unlimited agents' },
  ],
  costAt: (agents: number) => agents * 23, // Professional tier
  verdictKora: 'Teams who want AI-first ticket structuring, usage-based pricing, and embedded sessions without per-seat costs.',
  verdictCompetitor: 'Teams already using Zoho CRM/Books/Projects who want multi-department ticketing, SLA management, and the cheapest per-seat pricing.',
  koraWins: [
    'Pay-as-you-go credit pricing — no per-seat scaling costs',
    'AI ticket structuring is free — no per-seat or add-on fee',
    'No per-seat fees and no per-resolution billing — Kora charges $0.10 per ticket created',
    'Unlimited members, no seat fees (Zoho Free is 3 agents)',
    'Embedded sessions for in-app support',
    'Public REST API — 20+ endpoints, available to every workspace',
  ],
  competitorWins: [
    'Cheapest paid tier ($7/agent/mo Express)',
    'Multi-department ticket management',
    'Built-in escalation rules (Kora covers this with free SLA breach alerts and automation rules)',
    'Native telephony and social channels (Kora: not on the roadmap)',
    'Deep Zoho ecosystem integration (CRM, Books, Projects)',
    'Community forums (Kora has a public help center, not forums)',
  ],
  switchingReasons: [
    'Per-seat pricing adds up — 15 agents on Professional costs $345/mo vs Kora\'s ~$60 usage-based estimate',
    'Zia AI requires Enterprise tier ($40/agent/mo) — Kora includes AI structuring on Free',
    'Setup is notoriously rough and the UI feels dated',
    'You want AI-first ticket structuring without upgrading to the top tier',
  ],
  faqs: [
    {
      q: 'Is Kora really cheaper than Zoho Desk?',
      a: 'For most teams, yes. Kora is pay-as-you-go ($1 = 10 credits; a ticket costs 1 credit) — a 15-agent team doing ~450 tickets/month pays roughly $45/mo. Zoho Desk Professional is $23/agent/mo — 15 agents costs $345/mo. Kora also starts with 1,100 free credits.',
    },
    {
      q: 'Does Kora have multi-department support like Zoho Desk?',
      a: 'No. Kora uses a single workspace with categories and teams. Zoho Desk supports multiple departments (Sales, Billing, Support) each with their own queues, SLAs, and agents. If multi-department routing is essential, Zoho Desk is the stronger choice.',
    },
    {
      q: 'Can I import my tickets from Zoho Desk?',
      a: 'Yes. Export your Zoho Desk tickets as CSV, then paste raw descriptions into Kora (AI will structure them) or use the Public API to create tickets programmatically.',
    },
    {
      q: "What's the catch with Kora's free credits?",
      a: 'No catch. New workspaces get 1,100 free credits, no card required (1,000 valid for 30 days, 100 valid for 10 years). After that a ticket costs $0.10 and members are always free. AI ticket structuring, SLA tracking, automation rules, webhooks and embedded sessions are free; the AI Analyst is 1 credit per answer.',
    },
    {
      q: 'Does Kora integrate with the Zoho ecosystem?',
      a: 'Kora has outbound webhooks and a Public REST API (20+ endpoints) for every workspace. It does not have native integrations with Zoho CRM, Zoho Books, or other Zoho apps. If you rely on the Zoho ecosystem, Zoho Desk is the better fit.',
    },
    {
      q: 'Which is better for a small team?',
      a: 'For teams that want AI to structure tickets and pay per ticket instead of per seat, Kora. For teams who need multi-department routing, SLA tracking, and Zoho ecosystem integration, Zoho Desk — but expect per-seat costs to scale.',
    },
  ],
  atAGlance: [
    { label: 'Pricing model', kora: 'Pay-as-you-go ($1 = 10 credits)', competitor: 'Per agent/mo' },
    { label: 'Free to start', kora: '1,100 free credits', competitor: 'Free, 3 agents' },
    { label: 'AI ticket structuring', kora: 'Free (no add-on fee)', competitor: 'Zia AI on Enterprise ($40/agent)' },
    { label: 'AI Analyst', kora: '1 credit per answer', competitor: false },
    { label: 'Public API (20+ endpoints)', kora: 'Every workspace', competitor: 'Standard+' },
    { label: 'Embedded sessions', kora: 'Yes', competitor: 'No' },
    { label: 'Omnichannel', kora: 'Planned (email-to-ticket, Slack, live chat)', competitor: 'Native (email, chat, phone, social)' },
    { label: 'Knowledge base', kora: true, competitor: 'Yes' },
    { label: 'SLA tracking', kora: 'Free, every workspace', competitor: 'Yes (Professional+)' },
    { label: 'EU data residency', kora: 'Yes (Cloudflare D1, EU)', competitor: 'Varies (US, EU, IN, JP, AU)' },
  ],
  features: [
    {
      category: 'Ticketing & Workflow',
      rows: [
        { label: 'Ticket creation', kora: true, competitor: true },
        { label: 'Ticket assignment', kora: 'Claim model', competitor: 'Round-robin, skill-based' },
        { label: 'Ticket merging', kora: true, competitor: true },
        { label: 'Bulk ticket actions', kora: true, competitor: true },
        { label: 'Multi-department', kora: false, competitor: 'Professional+' },
        { label: 'SLA tracking', kora: 'Free, every workspace', competitor: 'Professional+' },
        { label: 'Escalation rules', kora: 'Via automation rules', competitor: true },
      ],
    },
    {
      category: 'AI',
      rows: [
        { label: 'AI ticket structuring', kora: 'Free (no add-on fee)', competitor: 'Zia AI on Enterprise ($40/agent)' },
        { label: 'AI Analyst (text-to-SQL)', kora: '1 credit per answer', competitor: false },
        { label: 'AI Copilot (agent assist)', kora: 'Kora AI draft reply: 1 credit', competitor: 'Enterprise+' },
        { label: 'Generative AI (BYOK)', kora: false, competitor: 'Express+ (OpenAI/DeepSeek)' },
      ],
    },
    {
      category: 'Collaboration',
      rows: [
        { label: 'Comments & attachments', kora: true, competitor: true },
        { label: 'Real-time updates', kora: true, competitor: true },
        { label: 'Internal notes', kora: true, competitor: true },
        { label: '@mentions', kora: true, competitor: true },
      ],
    },
    {
      category: 'Integrations & API',
      rows: [
        { label: 'Public REST API (20+ endpoints)', kora: 'Every workspace', competitor: 'Standard+' },
        { label: 'Webhooks (outbound)', kora: true, competitor: true },
        { label: 'Zoho ecosystem', kora: 'Via free API', competitor: 'Native (CRM, Books, Projects)' },
        { label: 'Custom API endpoints', kora: false, competitor: false },
      ],
    },
    {
      category: 'Embedding',
      rows: [
        { label: 'Embedded sessions (iframe)', kora: true, competitor: false },
        { label: 'Customer portal', kora: true, competitor: true },
        { label: 'White-label', kora: '~', competitor: 'Enterprise' },
      ],
    },
    {
      category: 'Analytics',
      rows: [
        { label: 'Dashboards', kora: true, competitor: true },
        { label: 'Custom reports', kora: '1 credit per answer via AI Analyst', competitor: 'Professional+' },
        { label: 'CSV export', kora: true, competitor: true },
        { label: 'Team analytics', kora: true, competitor: true },
      ],
    },
    {
      category: 'Channels',
      rows: [
        { label: 'Email', kora: 'Outbound notifications', competitor: 'Native inbound + outbound' },
        { label: 'Live chat', kora: 'Planned', competitor: 'Business Messaging' },
        { label: 'Phone / telephony', kora: 'Not available', competitor: true },
        { label: 'Social media', kora: 'Not available', competitor: true },
        { label: 'Community forums', kora: 'Not available', competitor: true },
      ],
    },
    {
      category: 'Security & Compliance',
      rows: [
        { label: 'SSO', kora: false, competitor: 'Enterprise' },
        { label: 'Audit logs', kora: 'Ticket activity log only', competitor: 'Enterprise' },
        { label: 'Sandbox', kora: false, competitor: false },
        { label: 'EU data residency', kora: true, competitor: '~' },
        { label: 'Encryption at rest', kora: true, competitor: true },
      ],
    },
  ],
};

// ─── Zendesk ─────────────────────────────────────────────────

export const zendesk: CompetitorData = {
  slug: 'kora-vs-zendesk',
  name: 'Zendesk',
  tagline: 'Enterprise customer service platform',
  bestFor: 'Large support teams (50+ agents) needing deep analytics, multi-brand, telephony, workforce management, and enterprise governance.',
  pricingModel: 'Per agent / month',
  plans: [
    { name: 'Support Team', price: 19, priceNote: 'Email + ticketing only' },
    { name: 'Suite Team', price: 55, priceNote: 'Omnichannel base' },
    { name: 'Suite Growth', price: 89, priceNote: 'SLAs, CSAT, light agents' },
    { name: 'Suite Professional', price: 115, priceNote: 'Skills routing, IVR, advanced reporting', highlight: true },
    { name: 'Suite Enterprise', price: 169, priceNote: 'Sandbox, custom roles, multi-brand' },
  ],
  costAt: (agents: number) => agents * 115, // Suite Professional tier
  verdictKora: 'Small-to-mid teams who want AI-first ticketing, usage-based pricing, and embedded sessions without per-seat costs.',
  verdictCompetitor: 'Enterprise teams (50+ agents) needing deep analytics, multi-brand support, telephony, workforce management, and 1500+ app integrations.',
  koraWins: [
    'Pay-as-you-go credit pricing — 15 agents on Zendesk Pro costs $1,725/mo',
    'AI ticket structuring is free — no per-seat or add-on fee',
    'No per-seat fees and no per-resolution billing — Kora charges $0.10 per ticket created',
    '1,100 free credits to start (Zendesk has no free plan — 14-day trial only)',
    'Embedded sessions for in-app support',
    'Public REST API — 20+ endpoints, available to every workspace',
    'Predictable costs — no per-resolution AI billing',
  ],
  competitorWins: [
    'Most mature help desk ecosystem (1500+ apps)',
    'Deepest analytics and custom reporting',
    'Native omnichannel today: email, chat, phone, social (Kora: email-to-ticket, Slack and live chat are planned; phone and social are not on the roadmap)',
    'Multi-brand support and sandbox environments',
    'Workforce management and quality assurance',
    'Enterprise-grade security, audit logs, and compliance',
  ],
  switchingReasons: [
    'Zendesk is the most expensive option — 15 agents on Suite Professional costs $1,725/mo vs Kora\'s ~$60 usage-based estimate',
    'AI is billed per resolution (~$1.50-2.00/resolution) on top of seat fees — costs are unpredictable',
    'Real cost is 2-3x list price once you add Copilot ($50/agent), WFM, and other add-ons',
    'No free plan — only a 14-day trial. Kora starts with 1,100 free credits and has no seat fees.',
    'Complex setup requires dedicated admins and training',
  ],
  faqs: [
    {
      q: 'Is Kora really cheaper than Zendesk?',
      a: 'Dramatically. Kora is pay-as-you-go ($1 = 10 credits; a ticket costs 1 credit) — a 15-agent team doing ~450 tickets/month pays roughly $45/mo. Zendesk Suite Professional is $115/agent/mo — 15 agents costs $1,725/mo. Kora also starts with 1,100 free credits; Zendesk has no free plan (14-day trial only).',
    },
    {
      q: 'Does Kora have Zendesk-level analytics?',
      a: 'No. Zendesk has the deepest analytics in the help desk market — custom dashboards, QA, workforce management, and AI insights. Kora has dashboards, team analytics, CSV export, and an AI Analyst for natural-language queries. For enterprise analytics, Zendesk is stronger.',
    },
    {
      q: 'Can I import my tickets from Zendesk?',
      a: 'Yes. Export your Zendesk tickets as CSV, then paste raw descriptions into Kora (AI will structure them) or use the Public API to create tickets programmatically. Comments and attachments can be migrated via the API.',
    },
    {
      q: "What's the catch with Kora's free credits?",
      a: 'No catch. New workspaces get 1,100 free credits, no card required (1,000 valid for 30 days, 100 valid for 10 years). After that a ticket costs $0.10 and members are always free. AI ticket structuring, SLA tracking, automation rules, webhooks and embedded sessions are free; the AI Analyst is 1 credit per answer.',
    },
    {
      q: 'Does Kora have Zendesk\'s AI features?',
      a: 'Kora includes free AI ticket structuring and an AI Analyst for natural-language data questions (1 credit per answer). Kora AI reply drafting (1 credit per draft) and a resolution agent (3 credits per confirmed resolution). Zendesk charges US$2.00 per automated resolution pay-as-you-go (zendesk.com/pricing, checked 28 Oct 2026), plus Copilot at $50/agent/mo. Zendesk\'s AI is more feature-rich but significantly more expensive.',
    },
    {
      q: 'Which is better for enterprise?',
      a: 'For teams under 50 who want AI-first workflows and usage-based pricing, Kora. For 50+ agent teams needing multi-brand, sandbox, workforce management, telephony, and deep analytics, Zendesk — budget for $115-169/agent/mo plus add-ons.',
    },
  ],
  atAGlance: [
    { label: 'Pricing model', kora: 'Pay-as-you-go ($1 = 10 credits)', competitor: 'Per agent/mo' },
    { label: 'Free to start', kora: '1,100 free credits', competitor: 'No (14-day trial)' },
    { label: 'AI ticket structuring', kora: 'Free (no add-on fee)', competitor: 'Add-on ($50/agent/mo Copilot)' },
    { label: 'AI Analyst', kora: '1 credit per answer', competitor: 'AI Insights (Enterprise)' },
    { label: 'Public API (20+ endpoints)', kora: 'Every workspace', competitor: 'All plans' },
    { label: 'Embedded sessions', kora: 'Yes', competitor: 'No' },
    { label: 'Omnichannel', kora: 'Planned (email-to-ticket, Slack, live chat)', competitor: 'Native (email, chat, phone, social)' },
    { label: 'Knowledge base', kora: true, competitor: 'Yes' },
    { label: 'SLA tracking', kora: 'Free, every workspace', competitor: 'Yes (Suite Growth+)' },
    { label: 'EU data residency', kora: 'Yes (Cloudflare D1, EU)', competitor: 'Varies by region' },
  ],
  features: [
    {
      category: 'Ticketing & Workflow',
      rows: [
        { label: 'Ticket creation', kora: true, competitor: true },
        { label: 'Ticket assignment', kora: 'Claim model', competitor: 'Skills-based, round-robin' },
        { label: 'Ticket merging', kora: true, competitor: true },
        { label: 'Bulk ticket actions', kora: true, competitor: true },
        { label: 'Custom workflows', kora: '~', competitor: true },
        { label: 'SLA tracking', kora: 'Free, every workspace', competitor: 'Suite Growth+' },
        { label: 'CSAT surveys', kora: false, competitor: true },
      ],
    },
    {
      category: 'AI',
      rows: [
        { label: 'AI ticket structuring', kora: 'Free (no add-on fee)', competitor: 'Add-on ($50/agent/mo Copilot)' },
        { label: 'AI Analyst (text-to-SQL)', kora: '1 credit per answer', competitor: false },
        { label: 'AI Copilot (agent assist)', kora: 'Kora AI draft reply: 1 credit', competitor: 'Add-on ($50/agent/mo)' },
        { label: 'AI Agent (deflection)', kora: 'Kora AI agent: 3 credits per confirmed resolution', competitor: '$2.00 per resolution pay-as-you-go' },
      ],
    },
    {
      category: 'Collaboration',
      rows: [
        { label: 'Comments & attachments', kora: true, competitor: true },
        { label: 'Real-time updates', kora: true, competitor: true },
        { label: 'Internal notes', kora: true, competitor: true },
        { label: 'Light agents', kora: false, competitor: 'Suite Growth+' },
      ],
    },
    {
      category: 'Integrations & API',
      rows: [
        { label: 'Public REST API (20+ endpoints)', kora: 'Every workspace', competitor: 'All plans' },
        { label: 'Webhooks (outbound)', kora: true, competitor: true },
        { label: 'Marketplace apps', kora: 'Not available', competitor: '1500+ apps' },
        { label: 'Custom API endpoints', kora: false, competitor: false },
      ],
    },
    {
      category: 'Embedding',
      rows: [
        { label: 'Embedded sessions (iframe)', kora: true, competitor: false },
        { label: 'Customer portal', kora: true, competitor: true },
        { label: 'White-label', kora: '~', competitor: 'Enterprise' },
      ],
    },
    {
      category: 'Analytics',
      rows: [
        { label: 'Dashboards', kora: true, competitor: true },
        { label: 'Custom reports', kora: '1 credit per answer via AI Analyst', competitor: 'Suite Professional+' },
        { label: 'CSV export', kora: true, competitor: true },
        { label: 'Team analytics', kora: true, competitor: true },
        { label: 'Workforce management', kora: false, competitor: 'Add-on' },
      ],
    },
    {
      category: 'Channels',
      rows: [
        { label: 'Email', kora: 'Outbound notifications', competitor: 'Native inbound + outbound' },
        { label: 'Live chat', kora: 'Planned', competitor: 'Suite Team+' },
        { label: 'Phone / telephony', kora: 'Not available', competitor: 'Suite Team+' },
        { label: 'Social media', kora: 'Not available', competitor: true },
        { label: 'IVR phone tree', kora: 'Not available', competitor: 'Suite Professional+' },
      ],
    },
    {
      category: 'Security & Compliance',
      rows: [
        { label: 'SSO', kora: false, competitor: 'Suite Enterprise' },
        { label: 'Audit logs', kora: 'Ticket activity log only', competitor: 'Suite Enterprise' },
        { label: 'Sandbox', kora: false, competitor: 'Suite Enterprise' },
        { label: 'Multi-brand', kora: false, competitor: 'Suite Enterprise' },
        { label: 'EU data residency', kora: true, competitor: '~' },
        { label: 'Encryption at rest', kora: true, competitor: true },
      ],
    },
  ],
};

// ─── Jira Service Management ────────────────────────────────

export const jiraSM: CompetitorData = {
  slug: 'kora-vs-jira-service-management',
  name: 'Jira Service Management',
  tagline: 'ITSM platform by Atlassian',
  bestFor: 'Engineering and IT teams already using Jira Software, Confluence, or Bitbucket who need ITIL workflows, change management, and on-call alerting.',
  pricingModel: 'Per agent / month',
  plans: [
    { name: 'Free', price: 0, priceNote: 'Up to 3 agents' },
    { name: 'Standard', price: 22.5, priceNote: 'Annual billing, 11-15 agent tier', highlight: true },
    { name: 'Premium', price: 51.1, priceNote: 'Annual billing, 11-15 agent tier' },
    { name: 'Enterprise', price: -1, priceNote: 'Contact us' },
  ],
  costAt: (agents: number) => agents * 22.5, // Standard tier (annual billing, 11-15 agent tier)
  verdictKora: 'Product, ops, and support teams who want AI-first ticketing, usage-based pricing, and embedded sessions without per-seat costs.',
  verdictCompetitor: 'Engineering and IT teams already in the Atlassian stack who need ITIL workflows, change management, on-call alerting, and asset/CMDB management.',
  koraWins: [
    'Pay-as-you-go credit pricing — 15 agents on Jira Standard costs about $338/mo',
    'AI ticket structuring is free — no per-seat or add-on fee',
    'No per-seat fees and no per-resolution billing — Kora charges $0.10 per ticket created',
    'Unlimited members, no seat fees (Jira Free is 3 agents)',
    'Embedded sessions for in-app support',
    'Simpler setup — no ITIL templates to configure',
    'Public REST API — 20+ endpoints, available to every workspace',
  ],
  competitorWins: [
    'Deep Atlassian stack integration (Jira Software, Confluence, Bitbucket)',
    'ITIL/ITSM templates (incident, problem, change management)',
    'On-call scheduling and alerting (Opsgenie built in)',
    'Assets and configuration management (CMDB)',
    'Free for end-users submitting requests (only agents pay)',
    'Atlassian Intelligence AI included on Standard+ at no extra cost',
  ],
  switchingReasons: [
    'Per-agent pricing adds up — 15 agents on Standard costs about $338/mo vs Kora\'s ~$60 usage-based estimate',
    'Change management requires Premium ($51.42/agent/mo) — a 158% jump from Standard',
    'Setup is complex and requires ITIL knowledge — not built for product or support teams',
    'You want AI-first ticket structuring without configuring ITSM templates',
    'Marketplace apps add real cost on top of the base subscription',
  ],
  faqs: [
    {
      q: 'Is Kora really cheaper than Jira Service Management?',
      a: 'For most teams, yes. Kora is pay-as-you-go ($1 = 10 credits; a ticket costs 1 credit) — a 15-agent team doing ~450 tickets/month pays roughly $45/mo. Jira Standard is about $22.50/agent/mo on annual billing — 15 agents costs about $338/mo (Atlassian pricing page, checked 2026-10-21). Kora starts with 1,100 free credits; Jira Free supports 3 agents.',
    },
    {
      q: 'Does Kora have ITSM features like change management?',
      a: 'No. Kora is a general-purpose issue tracker, not an ITSM platform. Jira Service Management has ITIL templates for incident, problem, and change management, on-call scheduling, and a CMDB. If ITSM workflows are essential, Jira is the stronger choice.',
    },
    {
      q: 'Can I import my tickets from Jira Service Management?',
      a: 'Yes. Export your Jira issues as CSV, then paste raw descriptions into Kora (AI will structure them) or use the Public API to create tickets programmatically.',
    },
    {
      q: "What's the catch with Kora's free credits?",
      a: 'No catch. New workspaces get 1,100 free credits, no card required (1,000 valid for 30 days, 100 valid for 10 years). After that a ticket costs $0.10 and members are always free. AI ticket structuring, SLA tracking, automation rules, webhooks and embedded sessions are free; the AI Analyst is 1 credit per answer.',
    },
    {
      q: 'Does Kora integrate with the Atlassian stack?',
      a: 'Kora has outbound webhooks and a Public REST API (20+ endpoints) for every workspace. It does not have native integrations with Jira Software, Confluence, or Bitbucket. If you rely on the Atlassian stack, Jira Service Management is the better fit.',
    },
    {
      q: 'Which is better for a product or support team?',
      a: 'For product, ops, or customer support teams who want AI to structure tickets and keep costs predictable, Kora. For IT and engineering teams who need ITIL workflows, change management, and Atlassian stack integration, Jira Service Management.',
    },
  ],
  atAGlance: [
    { label: 'Pricing model', kora: 'Pay-as-you-go ($1 = 10 credits)', competitor: 'Per agent/mo' },
    { label: 'Free to start', kora: '1,100 free credits', competitor: 'Free, 3 agents' },
    { label: 'AI ticket structuring', kora: 'Free (no add-on fee)', competitor: 'Atlassian Intelligence (Standard+)' },
    { label: 'AI Analyst', kora: '1 credit per answer', competitor: false },
    { label: 'Public API (20+ endpoints)', kora: 'Every workspace', competitor: 'All plans' },
    { label: 'Embedded sessions', kora: 'Yes', competitor: 'No' },
    { label: 'ITSM templates', kora: 'No', competitor: 'Yes (incident, problem, change)' },
    { label: 'On-call / alerting', kora: 'No', competitor: 'Yes (Opsgenie built in)' },
    { label: 'SLA tracking', kora: 'Free, every workspace', competitor: 'Yes (Standard+)' },
    { label: 'EU data residency', kora: 'Yes (Cloudflare D1, EU)', competitor: 'Multi-region (Premium+)' },
  ],
  features: [
    {
      category: 'Ticketing & Workflow',
      rows: [
        { label: 'Ticket creation', kora: true, competitor: true },
        { label: 'Ticket assignment', kora: 'Claim model', competitor: 'Round-robin, skill-based' },
        { label: 'Ticket merging', kora: true, competitor: true },
        { label: 'Bulk ticket actions', kora: true, competitor: true },
        { label: 'ITSM templates', kora: false, competitor: 'Incident, problem, change' },
        { label: 'SLA tracking', kora: 'Free, every workspace', competitor: 'Standard+' },
        { label: 'Change management', kora: false, competitor: 'Premium+' },
      ],
    },
    {
      category: 'AI',
      rows: [
        { label: 'AI ticket structuring', kora: 'Free (no add-on fee)', competitor: 'Atlassian Intelligence (Standard+)' },
        { label: 'AI Analyst (text-to-SQL)', kora: '1 credit per answer', competitor: false },
        { label: 'AI Copilot (agent assist)', kora: 'Kora AI draft reply: 1 credit', competitor: 'Standard+ (included)' },
        { label: 'Ticket summarization', kora: false, competitor: 'Standard+ (included)' },
      ],
    },
    {
      category: 'Collaboration',
      rows: [
        { label: 'Comments & attachments', kora: true, competitor: true },
        { label: 'Real-time updates', kora: true, competitor: true },
        { label: 'Internal notes', kora: true, competitor: true },
        { label: '@mentions', kora: true, competitor: true },
      ],
    },
    {
      category: 'Integrations & API',
      rows: [
        { label: 'Public REST API (20+ endpoints)', kora: 'Every workspace', competitor: 'All plans' },
        { label: 'Webhooks (outbound)', kora: true, competitor: true },
        { label: 'Atlassian stack', kora: 'Via free API', competitor: 'Native (Jira, Confluence, Bitbucket)' },
        { label: 'Marketplace apps', kora: 'Not available', competitor: '1000+ apps' },
        { label: 'Custom API endpoints', kora: false, competitor: false },
      ],
    },
    {
      category: 'Embedding',
      rows: [
        { label: 'Embedded sessions (iframe)', kora: true, competitor: false },
        { label: 'Customer portal', kora: true, competitor: true },
        { label: 'White-label', kora: '~', competitor: '~' },
      ],
    },
    {
      category: 'Analytics',
      rows: [
        { label: 'Dashboards', kora: true, competitor: true },
        { label: 'Custom reports', kora: '1 credit per answer via AI Analyst', competitor: true },
        { label: 'CSV export', kora: true, competitor: true },
        { label: 'Team analytics', kora: true, competitor: true },
      ],
    },
    {
      category: 'Channels',
      rows: [
        { label: 'Email', kora: 'Outbound notifications', competitor: 'Native inbound + outbound' },
        { label: 'Live chat', kora: 'Planned', competitor: 'Microsoft Teams, Slack' },
        { label: 'Phone / telephony', kora: 'Not available', competitor: false },
        { label: 'Customer portal', kora: true, competitor: true },
        { label: 'On-call alerting', kora: false, competitor: 'Opsgenie built in' },
      ],
    },
    {
      category: 'Security & Compliance',
      rows: [
        { label: 'SSO', kora: false, competitor: 'Enterprise' },
        { label: 'Audit logs', kora: 'Ticket activity log only', competitor: 'Standard+' },
        { label: 'Sandbox', kora: false, competitor: false },
        { label: 'Multi-region data residency', kora: 'EU (Cloudflare D1)', competitor: 'Premium+' },
        { label: 'Encryption at rest', kora: true, competitor: true },
      ],
    },
  ],
};

// Expanded competitors are loaded from expanded-data.ts to avoid circular imports.
// See expanded-data.ts for the full list of 50+ competitors.
import { EXPANDED_COMPETITORS } from './expanded-data';

export const ALL_COMPETITORS: CompetitorData[] = [freshdesk, zohoDesk, zendesk, jiraSM, ...EXPANDED_COMPETITORS];

export const LAST_VERIFIED = 'June 2026';
